import type { FC, ReactNode } from "react";

import sum from "lodash/sum";
import styled from "styled-components";

import { colorsSets } from "@similarweb/styles";
import { PopupHoverContainer } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

export const normalizeValues = (values: Array<number | number[]>, inverted = false): number[] => {
    const flattenValues = values.map((v) => {
        if (Array.isArray(v)) {
            return sum(v);
        } else {
            return v;
        }
    });

    if (inverted) {
        const minValue = Math.min(...flattenValues);

        return flattenValues.map((value) => 100 * (minValue / value));
    }

    const maxValue = Math.max(...flattenValues);

    return flattenValues.map((value) => 100 * (value / maxValue));
};

export interface ITrendsBarValue {
    value: number | number[];
    tooltip?: ReactNode;
}

interface ITrendsBarProps {
    values: ITrendsBarValue[];
    barsLimit?: number;
    inverted?: boolean;
    colorsSet?: string[];
}

export const Bar = styled.div.attrs<{
    height: number;
    index: number;
    color: string;
    multi: boolean;
    "data-automation-trend-bar"?: string;
}>((props) => ({
    "data-automation-trend-bar": props.index.toString(),
}))<{ height: number; index: number; color: "string"; multi: boolean }>`
    height: ${({ height }) => `${height}%`};
    /* when we have small numbers vs one big number the small bars seems like the zero bars
    so we should give them a higher min-height to start with */
    min-height: ${({ multi }) => (multi ? `1px` : `2px`)};
    width: 5px;
    background-color: ${({ color }) => color};
    opacity: 1;
    cursor: pointer;
    margin-right: 4px;
`;

const BarWrapper = styled(Flex)`
    flex-direction: column-reverse;
    height: 100%;
    &:hover {
        ${Bar} {
            opacity: 1 !important;
        }
    }
`;

const TrendsBarContainer = styled(Flex)`
    align-items: flex-end;
    gap: 4px;
    height: 100%;
    justify-content: center;
    &:hover {
        ${Bar} {
            opacity: 0.6;
        }
    }
`;

type BarContainerSchema = {
    heights: number[];
    colorsSet: string[];
    multi: boolean;
    index: number;
};

const BarContainer: FC<BarContainerSchema> = ({ heights, colorsSet, multi, index }) => {
    return (
        <BarWrapper>
            {heights.map((h, innerIndex) => {
                const _multi = h > 0 ? false : multi;
                return (
                    <div
                        data-automation-trend-bar={index.toString()}
                        key={`${index}${innerIndex}`}
                        // using inline styles instead of styled components to avoid creating many new components
                        // thus avoiding performance issues
                        style={{
                            height: h + "%",
                            minHeight: _multi ? "1px" : "2px",
                            width: "4px",
                            backgroundColor: colorsSet[innerIndex],
                            opacity: 1,
                            cursor: "pointer",
                        }}
                    />
                );
            })}
        </BarWrapper>
    );
};

const defaultColorsSet = colorsSets.b1.toArray();

export const TrendsBar: FC<ITrendsBarProps> = ({ values = [], barsLimit, inverted, colorsSet = defaultColorsSet }) => {
    if (!values) {
        return <>N/A</>;
    }
    const normalized = normalizeValues(
        values.map((item) => item.value),
        inverted,
    );
    return (
        <TrendsBarContainer data-automation-item="trends-bar-container">
            {values
                .map((item, index) => {
                    const valueIsArray = Array.isArray(item.value);
                    let totalHeight = 0;
                    if (valueIsArray) {
                        totalHeight = sum(item.value as number[]);
                    }
                    const bars = valueIsArray ? (
                        <BarContainer
                            key={index}
                            index={index}
                            heights={(item.value as number[]).map((v) =>
                                totalHeight > 0 ? (v / totalHeight) * normalized[index] : 0,
                            )}
                            colorsSet={colorsSet}
                            multi={true}
                        />
                    ) : (
                        <BarContainer
                            key={index}
                            index={index}
                            heights={[normalized[index]]}
                            colorsSet={colorsSet}
                            multi={false}
                        />
                    );
                    if (item.tooltip && normalized[index] > 0) {
                        return (
                            <PopupHoverContainer
                                key={index}
                                content={() => (
                                    <div style={{ minWidth: "90px", backgroundColor: "#FFF" }}>{item.tooltip}</div>
                                )}
                                config={{
                                    enabled: true,
                                    placement: "top",
                                    cssClass: "Popup-element-wrapper--trendsBar",
                                }}
                            >
                                <div style={{ height: "100%", flexGrow: 1, maxWidth: "9px" }}>{bars}</div>
                            </PopupHoverContainer>
                        );
                    } else {
                        return bars;
                    }
                })
                .slice(barsLimit ? values.length - barsLimit : undefined)}
        </TrendsBarContainer>
    );
};
